import React, { useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatNumber, parseNumber, calculateCursorPosition } from '../utils/formatters';
import { InfoTooltip } from '../utils/InfoTooltip';
export function SimpleCalculator() {
  const navigate = useNavigate();
  // Estados para los 4 campos
  const [income, setIncome] = useState<number>(0);
  const [incomeDisplay, setIncomeDisplay] = useState<string>('');
  const [debt, setDebt] = useState<number>(0);
  const [debtDisplay, setDebtDisplay] = useState<string>('');
  const [propertyPrice, setPropertyPrice] = useState<number>(0);
  const [propertyDisplay, setPropertyDisplay] = useState<string>('');
  const [pie, setPie] = useState<number>(0);
  const [pieDisplay, setPieDisplay] = useState<string>('');
  // Refs para cursor
  const incomeRef = useRef<HTMLInputElement>(null);
  const debtRef = useRef<HTMLInputElement>(null);
  const propertyRef = useRef<HTMLInputElement>(null);
  const pieRef = useRef<HTMLInputElement>(null);
  // Handler genérico para inputs formateados
  const handleFormattedChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, ref: React.RefObject<HTMLInputElement>, oldDisplay: string, setNumeric: (n: number) => void, setDisplay: (s: string) => void) => {
    const input = e.target;
    const cursorStart = input.selectionStart || 0;
    const cursorEnd = input.selectionEnd || 0;
    const newRawValue = input.value;
    if (newRawValue.trim() === '') {
      setNumeric(0);
      setDisplay('');
      return;
    }
    const numericValue = parseNumber(newRawValue);
    setNumeric(numericValue);
    const formatted = formatNumber(numericValue);
    setDisplay(formatted);
    requestAnimationFrame(() => {
      if (ref.current) {
        const newPosition = calculateCursorPosition(oldDisplay, formatted, cursorStart, cursorEnd);
        ref.current.setSelectionRange(newPosition, newPosition);
      }
    });
  }, []);
  const handleIncomeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => handleFormattedChange(e, incomeRef, incomeDisplay, setIncome, setIncomeDisplay), [incomeDisplay, handleFormattedChange]);
  const handleDebtChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => handleFormattedChange(e, debtRef, debtDisplay, setDebt, setDebtDisplay), [debtDisplay, handleFormattedChange]);
  const handlePropertyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => handleFormattedChange(e, propertyRef, propertyDisplay, setPropertyPrice, setPropertyDisplay), [propertyDisplay, handleFormattedChange]);
  const handlePieChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => handleFormattedChange(e, pieRef, pieDisplay, setPie, setPieDisplay), [pieDisplay, handleFormattedChange]);
  const handleSubmit = () => {
    // Validar que todos los campos estén completos
    if (income <= 0 || propertyPrice <= 0 || pie <= 0) {
      return;
    }
    // Navegar a /flujo con los datos
    navigate('/flujo', {
      state: {
        income,
        debt,
        propertyPrice,
        pie
      }
    });
  };
  const isValid = income > 0 && propertyPrice > 0 && pie > 0;
  return <section id="calculadora" className="py-8 md:py-20 bg-white" data-id="element-1356">
      <div className="container mx-auto max-w-3xl px-4" data-id="element-1357">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8" data-id="element-1358">
          <h2 className="text-xl md:text-3xl font-bold text-[#0A1628] leading-tight" data-id="element-1359">
            Calcula tu probabilidad de aprobación
          </h2>
          <p className="text-sm md:text-base text-[#6B7280] mt-2 px-2" data-id="element-1360">
            Completa estos datos básicos y te mostraremos tus posibilidades
            reales de obtener un crédito hipotecario
          </p>
        </div>

        {/* Formulario Simple */}
        <div className="bg-[#E8EDF5] rounded-xl p-4 md:p-6 space-y-4 mb-6" data-id="element-1361">
          <div data-id="element-1362">
            <label className="block text-sm font-medium mb-2 flex items-center gap-2 flex-wrap" data-id="element-1363">
              <span data-id="element-1364">Renta líquida mensual</span>
              <span className="text-[#6B7280] text-xs" data-id="element-1365">(CLP)</span>
              <InfoTooltip content="Tu sueldo después de descuentos (AFP, salud, impuestos)." data-id="element-1366" />
            </label>
            <input ref={incomeRef} type="text" inputMode="numeric" value={incomeDisplay} onChange={handleIncomeChange} className="w-full px-4 py-3 md:py-2 border rounded-lg text-base font-semibold touch-manipulation" placeholder="1.200.000" data-id="element-1367" />
          </div>

          <div data-id="element-1368">
            <label className="block text-sm font-medium mb-2 flex items-center gap-2 flex-wrap" data-id="element-1369">
              <span data-id="element-1370">Deudas mensuales totales</span>
              <span className="text-[#6B7280] text-xs" data-id="element-1371">(CLP)</span>
              <InfoTooltip content="Suma de todas tus cuotas mensuales: tarjetas, créditos, etc." data-id="element-1372" />
            </label>
            <input ref={debtRef} type="text" inputMode="numeric" value={debtDisplay} onChange={handleDebtChange} className="w-full px-4 py-3 md:py-2 border rounded-lg font-medium touch-manipulation text-base" placeholder="250.000" data-id="element-1373" />
          </div>

          <div data-id="element-1374">
            <label className="block text-sm font-medium mb-2 flex items-center gap-2 flex-wrap" data-id="element-1375">
              <span data-id="element-1376">Precio de la propiedad</span>
              <span className="text-[#6B7280] text-xs" data-id="element-1377">(UF)</span>
              <InfoTooltip content="El valor de la propiedad que quieres comprar." data-id="element-1378" />
            </label>
            <input ref={propertyRef} type="text" inputMode="numeric" value={propertyDisplay} onChange={handlePropertyChange} className="w-full px-4 py-3 md:py-2 border rounded-lg font-semibold touch-manipulation text-base" placeholder="3.000" data-id="element-1379" />
          </div>

          <div data-id="element-1380">
            <label className="block text-sm font-medium mb-2 flex items-center gap-2 flex-wrap" data-id="element-1381">
              <span data-id="element-1382">Pie que puedes poner</span>
              <span className="text-[#6B7280] text-xs" data-id="element-1383">(UF)</span>
              <InfoTooltip content="El dinero que tienes ahorrado para dar como pie." data-id="element-1384" />
            </label>
            <input ref={pieRef} type="text" inputMode="numeric" value={pieDisplay} onChange={handlePieChange} className="w-full px-4 py-3 md:py-2 border rounded-lg font-medium touch-manipulation text-base" placeholder="600" data-id="element-1385" />
          </div>
        </div>

        {/* CTA */}
        <button onClick={handleSubmit} disabled={!isValid} className="w-full px-6 py-4 bg-[#2B5FD9] text-white font-semibold rounded-lg hover:bg-[#1e4bb8] transition disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1386">
          Evaluar mi crédito →
        </button>

        {!isValid && (income > 0 || debt > 0 || propertyPrice > 0 || pie > 0) && <p className="text-sm text-orange-600 text-center mt-3" data-id="element-1387">
              Por favor completa todos los campos obligatorios
            </p>}
      </div>
    </section>;
}